import { CalendarDays, MessageSquareMore, Plus, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useDDSMeList } from '@/api/queries/dds-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardHeader,
	InspectionListCardLoading,
	InspectionListFilters,
} from '@/components/inspections'
import {
	InspectionSummaryBadge,
	InspectionSummaryCard,
	InspectionSummaryCardAside,
	InspectionSummaryCardBody,
	InspectionSummaryCardField,
	InspectionSummaryCardHeader,
	InspectionSummaryCardTitle,
} from '@/components/inspections/inspection-summary-card'
import { Button } from '@/components/ui/button'
import { CardContent, CardFooter } from '@/components/ui/card'
import { ROUTES } from '@/constants/routes'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useProfileStore } from '@/store'
import { useDDSStore } from '@/store/dds-store'
import type { DDSMeListParams, DDSOutput, DDSScope, DDSType } from '@/types/dds'
import { capitalize } from '@/utils/capitalize'
import { formatDateToDisplay } from '@/utils/formatDate'

const typeConfig: Record<DDSType, { label: string; className: string }> = {
	DDS: {
		label: 'DDS',
		className:
			'bg-kaefer-honey-yellow/15 text-kaefer-pumpkin-orange border-kaefer-honey-yellow/30',
	},
	PIT_STOP: {
		label: 'PIT STOP',
		className:
			'bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue border-kaefer-sapphire-blue/30',
	},
	DIG: {
		label: 'DIG',
		className:
			'bg-kaefer-grass-green/15 text-kaefer-grass-green border-kaefer-grass-green/30',
	},
	DMI: {
		label: 'DMI',
		className: 'bg-kaefer-violet/15 text-kaefer-violet border-kaefer-violet/30',
	},
	DDS_GERAL: {
		label: 'DDS GERAL',
		className:
			'bg-kaefer-raspberry-pink/15 text-kaefer-raspberry-pink border-kaefer-raspberry-pink/30',
	},
}

const scopeConfig: Record<
	DDSScope,
	{ labelKey: string; descriptionKey: string }
> = {
	mine: {
		labelKey: 'ddsList.scopeMine',
		descriptionKey: 'ddsList.scopeMineDescription',
	},
	branch: {
		labelKey: 'ddsList.scopeBranch',
		descriptionKey: 'ddsList.scopeBranchDescription',
	},
	cr: {
		labelKey: 'ddsList.scopeCr',
		descriptionKey: 'ddsList.scopeCrDescription',
	},
}

const typeFilterOptions: Array<{ value: DDSType; labelKey: string }> = [
	{ value: 'DDS', labelKey: 'ddsList.typeDDS' },
	{ value: 'PIT_STOP', labelKey: 'ddsList.typePitStop' },
	{ value: 'DIG', labelKey: 'ddsList.typeDig' },
	{ value: 'DMI', labelKey: 'ddsList.typeDmi' },
	{ value: 'DDS_GERAL', labelKey: 'ddsList.typeDdsGeral' },
]

const DDSCard = ({ dds, onClick }: { dds: DDSOutput; onClick: () => void }) => {
	const { t } = useTranslation()
	const config = typeConfig[dds.type]

	return (
		<InspectionSummaryCard
			accentClassName="border-l-kaefer-honey-yellow"
			onClick={onClick}
		>
			<InspectionSummaryCardBody>
				<InspectionSummaryCardHeader
					badge={
						<InspectionSummaryBadge
							label={config.label}
							className={config.className}
						/>
					}
					topMeta={
						<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
							<Users className="size-3" />
							<span>
								{dds.attendeesCount}{' '}
								{dds.attendeesCount === 1
									? t('ddsList.participant')
									: t('ddsList.participants')}
							</span>
							<CalendarDays className="size-3.5" />
							{formatDateToDisplay(dds.date)}
						</span>
					}
				/>

				<InspectionSummaryCardTitle>{dds.subject}</InspectionSummaryCardTitle>

				<div className="flex items-center justify-between gap-2 text-xs">
					<div className="min-w-0 flex-1 space-y-2">
						<InspectionSummaryCardField
							label={t('ddsList.createdByLabel')}
							value={capitalize(dds.createdBy)}
						/>
						<InspectionSummaryCardField
							label={t('ddsList.branchLabel')}
							value={capitalize(dds.branchName)}
						/>
					</div>
					<InspectionSummaryCardAside />
				</div>
			</InspectionSummaryCardBody>
		</InspectionSummaryCard>
	)
}

export const DDSListPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { setDdsIdsAttendees } = useDDSStore()
	const { defaultBranchId, defaultContractId } = useProfileStore()
	const { data: organization } = useOrganization()

	const [selectedScope, setSelectedScope] = useState<DDSScope>('branch')
	const [selectedCrId, setSelectedCrId] = useState<string | undefined>()
	const [selectedType, setSelectedType] = useState<DDSType | undefined>()
	const [selectedCustomerId, setSelectedCustomerId] = useState<
		string | undefined
	>()
	const [search, setSearch] = useState('')
	const { debouncedValue: debouncedSearch, isPending: isSearchPending } =
		useDebouncedValue(search, 600)
	const [cursor, setCursor] = useState<string | undefined>()
	const [items, setItems] = useState<DDSOutput[]>([])
	const [facets, setFacets] = useState<{
		crs: Array<{ id: string; name: string; count: number }>
		branches: Array<{ id: string; name: string; count: number }>
		customers: Array<{ id: string; name: string; count: number }>
	}>({
		crs: [],
		branches: [],
		customers: [],
	})
	const [pageInfo, setPageInfo] = useState<{
		nextCursor: string | null
		hasNext: boolean
	}>({
		nextCursor: null,
		hasNext: false,
	})

	const selectedBranch = organization?.find(
		(branch) => branch.id === defaultBranchId,
	)
	const effectiveCrId = selectedCrId ?? defaultContractId
	const selectedCr = selectedBranch?.crs.find((cr) => cr.id === effectiveCrId)
	const shouldShowCustomerFilter = (selectedCr?.customers?.length ?? 0) > 0

	const queryParams = useMemo<DDSMeListParams>(
		() => ({
			scope: selectedScope,
			crId: selectedCrId,
			type: selectedType,
			customerId: shouldShowCustomerFilter ? selectedCustomerId : undefined,
			q: debouncedSearch.trim() || undefined,
			cursor,
		}),
		[
			selectedScope,
			selectedCrId,
			selectedType,
			selectedCustomerId,
			shouldShowCustomerFilter,
			debouncedSearch,
			cursor,
		],
	)

	const { data, isFetching } = useDDSMeList(queryParams)

	useEffect(() => {
		if (!data) return

		setFacets(data.facets)
		setPageInfo(data.pageInfo)
		setItems((previous) => {
			if (!cursor) return data.items

			const existingIds = new Set(previous.map((item) => item.id))
			return [
				...previous,
				...data.items.filter((item) => !existingIds.has(item.id)),
			]
		})
	}, [data, cursor])

	useEffect(() => {
		setDdsIdsAttendees([])
	}, [setDdsIdsAttendees])

	const handleBack = () => {
		navigate(ROUTES.DDS.HUB)
	}

	const handleNewDDS = () => {
		navigate(ROUTES.DDS.NEW)
	}

	const handleOpenDDS = (ddsId: string) => {
		navigate(ROUTES.DDS.DETAIL.replace(':id', ddsId))
	}

	const handleLoadMore = () => {
		if (!pageInfo.hasNext || !pageInfo.nextCursor || isFetching) return
		setCursor(pageInfo.nextCursor)
	}

	const handleScopeChange = (value: string) => {
		setSelectedScope(value as DDSScope)
		setSelectedCrId(undefined)
		setSelectedType(undefined)
		setSearch('')
		setCursor(undefined)
		setItems([])
		setPageInfo({ nextCursor: null, hasNext: false })
	}

	const handleCrChange = (value: string) => {
		setSelectedCrId(value || undefined)
		setCursor(undefined)
		setItems([])
		setPageInfo({ nextCursor: null, hasNext: false })
	}

	const handleSearchChange = (value: string) => {
		setSearch(value)
		setCursor(undefined)
		setItems([])
		setPageInfo({ nextCursor: null, hasNext: false })
	}

	const handleTypeChange = (value: string) => {
		setSelectedType((value as DDSType) || undefined)
		setCursor(undefined)
		setItems([])
		setPageInfo({ nextCursor: null, hasNext: false })
	}

	const handleCustomerChange = (value: string) => {
		setSelectedCustomerId(value || undefined)
		setCursor(undefined)
		setItems([])
		setPageInfo({ nextCursor: null, hasNext: false })
	}

	useEffect(() => {
		if (shouldShowCustomerFilter || !selectedCustomerId) return
		setSelectedCustomerId(undefined)
	}, [shouldShowCustomerFilter, selectedCustomerId])

	const totalItems = items.length
	const hasMore = pageInfo.hasNext && Boolean(pageInfo.nextCursor)
	const isLoadingMore = isFetching && Boolean(cursor)
	const footerText = `${totalItems} ${
		totalItems === 1 ? t('ddsList.countSingular') : t('ddsList.countPlural')
	}`

	return (
		<InspectionCard colors={INSPECTION_COLORS.honeyYellow}>
			<InspectionCardHeader
				icon={<MessageSquareMore className="size-5" />}
				title={t('ddsList.title')}
				subtitle={t('ddsList.subtitle')}
				colors={INSPECTION_COLORS.honeyYellow}
				onBack={handleBack}
				actions={
					<Button
						onClick={handleNewDDS}
						className="w-full gap-2 bg-kaefer-honey-yellow font-semibold text-kaefer-dark-grey hover:bg-kaefer-honey-yellow/90 sm:w-auto"
					>
						<Plus className="size-4" />
						{t('ddsList.newDDS')}
					</Button>
				}
			/>

			<CardContent className="flex-1 space-y-4">
				<InspectionListFilters
					searchValue={search}
					onSearchChange={handleSearchChange}
					selects={[
						{
							id: 'dds-scope-filter',
							label: t('ddsList.scopeLabel'),
							value: selectedScope,
							onChange: handleScopeChange,
							options: (Object.keys(scopeConfig) as DDSScope[]).map(
								(scope) => ({
									value: scope,
									label: t(scopeConfig[scope].labelKey),
								}),
							),
						},
						{
							id: 'dds-cr-filter',
							label: t('inspections.common.contract'),
							value: selectedCrId,
							onChange: handleCrChange,
							options: facets.crs.map((cr) => ({
								value: cr.id,
								label: cr.name,
								count: cr.count,
							})),
							allLabel: t('ddsList.filterAll'),
						},
						{
							id: 'dds-type-filter',
							label: t('ddsList.type'),
							value: selectedType,
							onChange: handleTypeChange,
							options: typeFilterOptions.map((option) => ({
								value: option.value,
								label: t(option.labelKey),
							})),
							allLabel: t('ddsList.typeAll'),
						},
						...(shouldShowCustomerFilter
							? [
									{
										id: 'dds-filter-customer',
										label: t('inspections.common.customer'),
										value: selectedCustomerId,
										onChange: handleCustomerChange,
										options: facets.customers.map((customer) => ({
											value: customer.id,
											label: customer.name,
											count: customer.count,
										})),
										allLabel: t('ddsList.filterAll'),
									},
								]
							: []),
					]}
				/>

				<div className="rounded-xl border border-dashed border-kaefer-light-silver bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
					{t(scopeConfig[selectedScope].descriptionKey)}
				</div>

				{isSearchPending ? (
					<InspectionListCardLoading type="dds" />
				) : items.length > 0 ? (
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
						{items.map((dds) => (
							<DDSCard
								key={dds.id}
								dds={dds}
								onClick={() => handleOpenDDS(dds.id)}
							/>
						))}
					</div>
				) : (
					<div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-kaefer-light-silver py-16">
						<MessageSquareMore className="size-10 text-kaefer-silver" />
						<p className="mt-3 text-sm text-muted-foreground">
							{t('ddsList.empty')}
						</p>
					</div>
				)}

				{hasMore && items.length > 0 && (
					<div className="flex justify-center">
						<Button
							variant="outline"
							onClick={handleLoadMore}
							disabled={isLoadingMore}
							className="gap-2"
						>
							{isLoadingMore ? t('ddsList.loadingMore') : t('ddsList.loadMore')}
						</Button>
					</div>
				)}
			</CardContent>

			<CardFooter className="mt-4 border-t border-kaefer-honey-yellow/30 bg-kaefer-honey-yellow/5 py-3">
				<p className="w-full text-xs text-muted-foreground">{footerText}</p>
			</CardFooter>
		</InspectionCard>
	)
}
